import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";
import { env } from "../config";
import Logger from "../utils/logger";
import { keycloak } from "../auth/keycloakService";

// Create an axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: env.API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    Logger.info("Sending API request", {
      url: config.url,
      method: config.method,
      baseURL: env.API_BASE_URL,
    });

    if (keycloak.token) {
      config.headers["Authorization"] = `Bearer ${keycloak.token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    Logger.error("Error during request", { error });
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    Logger.info("API response received", {
      url: response.config.url,
      method: response.config.method,
      status: response.status,
      data: response.data,
    });
    return response;
  },
  async (error: AxiosError) => {
    Logger.error("Error during API response", {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.message,
      data: error.response?.data,
    });

    const originalRequest = error.config;

    // Handle expired token
    if (
      error.response?.status === 401 &&
      !(originalRequest as any)._retry // Prevent infinite loop
    ) {
      (originalRequest as any)._retry = true;

      try {
        Logger.warn("401 detected. Attempting token refresh...");

        // Try to update token
        const refreshed = await keycloak.updateToken(10);
        if (refreshed) {
          Logger.info("Token refreshed. Retrying request...");
          (originalRequest as any).headers[
            "Authorization"
          ] = `Bearer ${keycloak.token}`;
          return apiClient(originalRequest as any);
        } else {
          throw new Error("Token not refreshed");
        }
      } catch (refreshError) {
        Logger.error("Token refresh failed. Redirecting to login...");
        keycloak.login(); // force re-login
      }
    }

    return Promise.reject(error);
  }
);

export { apiClient };
